/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { Component } from 'types/api-data';

import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import { Logo } from '@/components/cards';
import ArchitectureServiceDialog from '@/components/display-page/architecture/architecture-service-dialog';
import { Icons } from '@/components/icons';
import { DetailsPageSpinner } from '@/components/spinner/details-page-spinner';

import EllipsesDropdown from './ellipses-dropdown';
import { useWorkflowDependency } from './useWorkflowDependency';

const WorkflowService = ({
  component,
  workflowName,
}: {
  component: Component;
  workflowName: string;
}) => {
  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const compIntl = messages?.ComponentsPage;
  const {
    selectedService,
    showList,
    isLoading,
    isUpdating,
    isInitialLoading,
    matchedService,
    matchedServiceData,
    activeServices,
    commandRef,
    setShowList,
    handleUpdate,
    handleRemove,
  } = useWorkflowDependency({
    component,
    workflowName,
    type: 'service',
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const handleEndpointUrlClick = (url: string) => {
    setOpenDialog(true);
    setSelectedUrl(url);
  };

  if (isUpdating) {
    return (
      <Skeleton className="animate-pulse-custom mr-2 h-[50px] w-44 rounded-xl bg-[#8C9EA1]" />
    );
  }
  return (
    <div className="min-w-[198px]">
      {selectedService ? (
        <div>
          <div className="group flex items-center gap-2">
            <Logo
              type={selectedService.type}
              className="border border-[#40565A]"
              size={{ img: 'size-6', bg: 'size-10' }}
            />
            <div className="flex flex-col">
              <Link
                href={{
                  pathname: '/services',
                  query: {
                    search: encodeURIComponent(selectedService.display_name),
                  },
                }}
                className="group flex flex-col"
              >
                <h3 className="text-h6 text-form-input-disabled">
                  {generalIntl?.service}
                </h3>
                <span className="text-body font-medium text-body-primary group-hover:underline group-hover:underline-offset-2">
                  {selectedService.display_name}
                </span>
                <span className="text-body text-form-input-disabled">
                  {matchedService?.service_display_name}
                </span>
              </Link>
            </div>

            <EllipsesDropdown
              onEdit={() => setShowList(true)}
              onRemove={handleRemove}
            />
          </div>
        </div>
      ) : (
        <>
          <h3 className="text-body-sm text-form-input-disabled">
            {generalIntl?.services}
          </h3>
          <div
            className="inline-flex cursor-pointer items-center gap-1 text-link-active"
            onClick={() => setShowList(true)}
          >
            {Icons.Plus('stroke-0')}
            <span className="text-sm">{compIntl?.add_service}</span>
          </div>
        </>
      )}
      {showList && (
        <div className="absolute z-10 mt-2" ref={commandRef}>
          <Command className="min-w-[210px] rounded-sm rounded-b-none bg-white  text-[#464642]">
            <CommandInput
              placeholder={
                messages?.ProviderServiceEditDetailsDialog
                  ?.search_service_placeholder
              }
              className="text-[#323130] placeholder:text-[#8C9EA1]"
              iconColor="-ml-1"
            />
            {isLoading ? (
              <div className="p-4 text-center">
                <DetailsPageSpinner />
              </div>
            ) : (
              <CommandList className="max-h-40 overflow-y-auto">
                {activeServices?.map(
                  (service: {
                    id: number;
                    display_name: string;
                    type: string;
                  }) => (
                    <CommandItem
                      key={service.id}
                      className="flex cursor-pointer items-center gap-1.5 data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130]"
                      onSelect={() => handleUpdate(service)}
                    >
                      <Logo
                        type={service.type}
                        size={{ img: 'size-[14px]', bg: 'size-6' }}
                      />
                      <span className="text-[#201F1E]">
                        {service.display_name}
                      </span>
                    </CommandItem>
                  )
                )}
              </CommandList>
            )}
          </Command>
        </div>
      )}
      {isInitialLoading && selectedService ? (
        <div className="ml-12 mt-2">
          <Skeleton className="h-4 w-24 rounded bg-[#8C9EA1]" />
        </div>
      ) : (
        matchedServiceData?.properties?.component_url && (
          <div className="ml-12 mt-2">
            <button
              onClick={() => {
                const endpointUrl =
                  matchedServiceData.properties?.component_url;
                if (endpointUrl) {
                  handleEndpointUrlClick(endpointUrl);
                }
              }}
              className="flex items-center gap-2"
            >
              <span className="text-btn-label text-ocean hover:underline hover:underline-offset-2">
                {messages?.Architecture?.view_endpoint}
              </span>
              {Icons.PopOut()}
            </button>
          </div>
        )
      )}
      {selectedUrl && (
        <ArchitectureServiceDialog
          endpointUrl={selectedUrl}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
        />
      )}
    </div>
  );
};

export default WorkflowService;
